// src/services/reporting/surveyReports.service.ts
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { surveyService } from '../survey.service';
import {
  SurveyAnalyticsReport,
  QuestionAnalytics,
  DateRange,
} from '../../types/reporting.types';
import { SurveyResponse } from '../../types/survey.types';
import { format } from 'date-fns';

class SurveyReportsService {
  async generateSurveyAnalytics(
    surveyId: string,
    dateRange?: DateRange,
    projectId?: string
  ): Promise<SurveyAnalyticsReport | null> {
    const survey = await surveyService.get(surveyId);
    if (!survey) return null;

    // Get all responses for this survey, optionally filtered by project
    const responses = await this.getSurveyResponses(surveyId, dateRange, projectId);

    // Calculate response metrics
    const completedResponses = responses.filter(r => !r.discontinued);
    const discontinuedResponses = responses.filter(r => r.discontinued);

    const responseMetrics = {
      totalResponses: responses.length,
      completedResponses: completedResponses.length,
      discontinuedResponses: discontinuedResponses.length,
      completionRate: responses.length > 0
        ? Math.round((completedResponses.length / responses.length) * 100)
        : 0,
      avgCompletionTime: this.calculateAvgCompletionTime(completedResponses),
      responsesByDay: this.groupResponsesByDay(responses),
    };

    // Generate question-level analytics
    const questionAnalytics = survey.questions.map(question =>
      this.analyzeQuestion(question, responses)
    );

    // Group by workload
    const workloadDistribution = this.groupByWorkload(responses);

    // Group by canvasser
    const canvasserDistribution = this.groupByCanvasser(responses);

    return {
      surveyId,
      surveyName: survey.name,
      surveyVersion: survey.version,
      generatedAt: new Date(),
      dateRange: dateRange || { start: new Date(0), end: new Date() },
      responseMetrics,
      questionAnalytics,
      workloadDistribution,
      canvasserDistribution,
    };
  }

  private async getSurveyResponses(
    surveyId: string,
    dateRange?: DateRange,
    projectId?: string
  ): Promise<SurveyResponse[]> {
    const responsesRef = collection(db, 'surveyResponses');

    // Build query with surveyId and optionally projectId
    const constraints = [where('surveyId', '==', surveyId)];
    if (projectId) {
      constraints.push(where('projectId', '==', projectId));
    }

    const q = query(responsesRef, ...constraints);

    const snapshot = await getDocs(q);
    let responses = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as SurveyResponse[];

    // Filter by date range if provided
    if (dateRange) {
      responses = responses.filter(r => {
        const responseDate = r.completedAt?.toDate?.() || r.startedAt?.toDate?.() || new Date();
        return responseDate >= dateRange.start && responseDate <= dateRange.end;
      });
    }

    return responses;
  }

  private calculateAvgCompletionTime(responses: SurveyResponse[]): number {
    const completedWithTime = responses.filter(r => r.startedAt && r.completedAt);
    if (completedWithTime.length === 0) return 0;

    const totalMinutes = completedWithTime.reduce((sum, r) => {
      const start = r.startedAt?.toDate?.() || new Date();
      const end = r.completedAt?.toDate?.() || new Date();
      return sum + (end.getTime() - start.getTime()) / 60000;
    }, 0);

    return Math.round(totalMinutes / completedWithTime.length);
  }

  private groupResponsesByDay(responses: SurveyResponse[]): { date: string; count: number }[] {
    const byDay: Record<string, number> = {};

    responses.forEach(r => {
      const responseDate = r.completedAt?.toDate?.() || r.startedAt?.toDate?.() || new Date();
      // Use local timezone formatting instead of UTC
      const date = format(responseDate, 'yyyy-MM-dd');
      byDay[date] = (byDay[date] || 0) + 1;
    });

    return Object.entries(byDay)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }

  private analyzeQuestion(question: any, responses: SurveyResponse[]): QuestionAnalytics {
    // Build a lookup map from option ID/value to option text
    const optionLookup: Record<string, string> = {};
    if (question.options && Array.isArray(question.options)) {
      question.options.forEach((opt: any) => {
        if (opt.id) optionLookup[opt.id] = opt.text || opt.value || opt.id;
        if (opt.value) optionLookup[opt.value] = opt.text || opt.value;
        if (opt.text) optionLookup[opt.text] = opt.text;
      });
    }

    // Helper to resolve a response value to display text
    const resolveValue = (v: any): any => {
      if (v === undefined || v === null || v === 'undefined' || v === 'null') {
        return '';
      }
      if (Array.isArray(v)) {
        return v
          .filter(item => item !== undefined && item !== null && item !== 'undefined' && item !== 'null')
          .map(item => optionLookup[String(item)] || String(item));
      }
      const strVal = String(v);
      if (strVal === 'undefined' || strVal === 'null') return '';
      return optionLookup[strVal] || strVal;
    };

    // Helper to sanitize submitter name
    const sanitizeName = (name: any): string => {
      if (!name || name === 'undefined' || name === 'null' || name === 'Unknown') {
        return '';
      }
      return String(name);
    };

    // Collect all responses with metadata, resolving option IDs to text
    const responsesWithMeta = responses
      .filter(r => {
        const value = r.responses[question.id];
        return value !== undefined && value !== null && value !== '' && value !== 'undefined';
      })
      .map(r => ({
        value: resolveValue(r.responses[question.id]),
        submittedBy: sanitizeName(r.submittedByName) || sanitizeName(r.submittedBy) || '',
        submittedAt: r.completedAt?.toDate?.()?.toISOString() ||
                     r.startedAt?.toDate?.()?.toISOString() || '',
        addressId: r.addressId || '',
      }));

    const questionResponses = responsesWithMeta.map(r => r.value);

    const totalResponses = questionResponses.length;
    const skippedCount = responses.length - totalResponses;
    const responseRate = responses.length > 0
      ? Math.round((totalResponses / responses.length) * 100)
      : 0;

    const analytics: QuestionAnalytics = {
      questionId: question.id,
      questionText: question.text,
      questionType: question.type,
      required: question.required || false,
      totalResponses,
      skippedCount,
      responseRate,
      // Include all responses for display
      allResponses: responsesWithMeta,
    };

    // Add type-specific analytics
    switch (question.type) {
      case 'single_choice':
      case 'multi_choice':
      case 'multiple_choice':
      case 'multi_select':
      case 'dropdown':
      case 'yes_no':
        analytics.choiceDistribution = this.analyzeChoiceQuestion(questionResponses, question);
        break;

      case 'rating':
      case 'scale':
      case 'rating_scale':
        analytics.ratingStats = this.analyzeRatingQuestion(questionResponses);
        analytics.choiceDistribution = this.analyzeChoiceQuestion(
          questionResponses.map(String),
          question
        );
        break;

      case 'number':
        analytics.numericStats = this.analyzeNumericQuestion(questionResponses);
        analytics.choiceDistribution = this.analyzeChoiceQuestion(
          questionResponses.map(String),
          question
        );
        break;

      case 'text':
      case 'textarea':
      case 'email':
      case 'phone':
      case 'date':
        analytics.textStats = this.analyzeTextQuestion(questionResponses);
        break;

      default:
        // For any other question types, show as choice distribution if there are options
        if (questionResponses.length > 0) {
          if (question.options && question.options.length > 0) {
            analytics.choiceDistribution = this.analyzeChoiceQuestion(questionResponses, question);
          } else {
            analytics.choiceDistribution = this.analyzeChoiceQuestion(
              questionResponses.map(v => Array.isArray(v) ? v.join(', ') : String(v)),
              question
            );
          }
        }
        break;
    }

    return analytics;
  }

  private analyzeChoiceQuestion(
    responses: any[],
    question: any
  ): { value: string; count: number; percentage: number }[] {
    const counts: Record<string, number> = {};

    // Build a lookup map from option ID/value to option text
    const optionLookup: Record<string, string> = {};
    if (question.options && Array.isArray(question.options)) {
      question.options.forEach((opt: any) => {
        // Map both ID and value to text for flexible matching
        if (opt.id) optionLookup[opt.id] = opt.text || opt.value || opt.id;
        if (opt.value) optionLookup[opt.value] = opt.text || opt.value;
        // Also map text to itself in case text is stored
        if (opt.text) optionLookup[opt.text] = opt.text;
      });
    }

    // Helper to resolve a response value to display text
    const resolveValue = (v: any): string => {
      const strVal = String(v);
      return optionLookup[strVal] || strVal;
    };

    responses.forEach(response => {
      if (Array.isArray(response)) {
        // Multi-choice
        response.forEach(v => {
          const displayValue = resolveValue(v);
          counts[displayValue] = (counts[displayValue] || 0) + 1;
        });
      } else {
        // Single choice
        const displayValue = resolveValue(response);
        counts[displayValue] = (counts[displayValue] || 0) + 1;
      }
    });

    const total = responses.length;
    return Object.entries(counts)
      .map(([value, count]) => ({
        value,
        count,
        percentage: total > 0 ? Math.round((count / total) * 100) : 0,
      }))
      .sort((a, b) => b.count - a.count);
  }

  private analyzeRatingQuestion(responses: number[]): {
    avgRating: number;
    distribution: Record<number, number>;
    npsScore?: number;
  } {
    const validResponses = responses.filter(r => typeof r === 'number');
    if (validResponses.length === 0) {
      return { avgRating: 0, distribution: {} };
    }

    const sum = validResponses.reduce((a, b) => a + b, 0);
    const avgRating = Math.round((sum / validResponses.length) * 10) / 10;

    const distribution: Record<number, number> = {};
    validResponses.forEach(r => {
      distribution[r] = (distribution[r] || 0) + 1;
    });

    return { avgRating, distribution };
  }

  private analyzeNumericQuestion(responses: number[]): {
    min: number;
    max: number;
    avg: number;
    median: number;
    mode: number;
  } {
    const validResponses = responses.filter(r => typeof r === 'number');
    if (validResponses.length === 0) {
      return { min: 0, max: 0, avg: 0, median: 0, mode: 0 };
    }

    const sorted = [...validResponses].sort((a, b) => a - b);
    const sum = sorted.reduce((a, b) => a + b, 0);

    // Calculate mode
    const counts: Record<number, number> = {};
    sorted.forEach(v => {
      counts[v] = (counts[v] || 0) + 1;
    });
    const mode = Number(
      Object.entries(counts).reduce((a, b) => (b[1] > a[1] ? b : a))[0]
    );

    return {
      min: sorted[0],
      max: sorted[sorted.length - 1],
      avg: Math.round((sum / sorted.length) * 100) / 100,
      median: sorted[Math.floor(sorted.length / 2)],
      mode,
    };
  }

  private analyzeTextQuestion(responses: any[]): {
    avgLength: number;
    wordCloud: { word: string; count: number }[];
    sampleResponses: string[];
    allResponses: string[];
  } {
    const validResponses = responses
      .map(r => String(r || ''))
      .filter(r => r.trim());
    if (validResponses.length === 0) {
      return { avgLength: 0, wordCloud: [], sampleResponses: [], allResponses: [] };
    }

    // Average length
    const totalLength = validResponses.reduce((sum, r) => sum + r.length, 0);
    const avgLength = Math.round(totalLength / validResponses.length);

    // Word frequency (simple word cloud)
    const wordCounts: Record<string, number> = {};
    const stopWords = new Set([
      'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
      'of', 'with', 'by', 'from', 'is', 'are', 'was', 'were', 'be', 'been',
      'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would',
      'could', 'should', 'may', 'might', 'must', 'shall', 'can', 'need',
      'dare', 'ought', 'used', 'i', 'you', 'he', 'she', 'it', 'we', 'they',
      'me', 'him', 'her', 'us', 'them', 'my', 'your', 'his', 'its', 'our',
      'their', 'this', 'that', 'these', 'those', 'what', 'which', 'who',
      'whom', 'whose', 'where', 'when', 'why', 'how', 'all', 'each', 'every',
      'both', 'few', 'more', 'most', 'other', 'some', 'such', 'no', 'nor',
      'not', 'only', 'own', 'same', 'so', 'than', 'too', 'very', 'just',
    ]);

    validResponses.forEach(response => {
      const words = response.toLowerCase().split(/\W+/).filter(w => w.length > 2 && !stopWords.has(w));
      words.forEach(word => {
        wordCounts[word] = (wordCounts[word] || 0) + 1;
      });
    });

    const wordCloud = Object.entries(wordCounts)
      .map(([word, count]) => ({ word, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20); // Top 20 words

    // Sample responses (random 5)
    const sampleResponses = [...validResponses]
      .sort(() => Math.random() - 0.5)
      .slice(0, 5);

    return {
      avgLength,
      wordCloud,
      sampleResponses,
      allResponses: validResponses, // Include all responses
    };
  }

  private groupByWorkload(responses: SurveyResponse[]): {
    workloadId: string;
    workloadName: string;
    responseCount: number;
  }[] {
    const byWorkload: Record<string, { name: string; count: number }> = {};

    responses.forEach(r => {
      if (r.workloadId && r.workloadId !== 'undefined') {
        if (!byWorkload[r.workloadId]) {
          const name = r.workloadName && r.workloadName !== 'undefined' ? r.workloadName : 'Unknown Workload';
          byWorkload[r.workloadId] = { name, count: 0 };
        }
        byWorkload[r.workloadId].count++;
      }
    });

    return Object.entries(byWorkload)
      .map(([workloadId, data]) => ({
        workloadId,
        workloadName: data.name,
        responseCount: data.count,
      }))
      .sort((a, b) => b.responseCount - a.responseCount);
  }

  private groupByCanvasser(responses: SurveyResponse[]): {
    userId: string;
    userName: string;
    responseCount: number;
    avgCompletionTime: number;
  }[] {
    const byCanvasser: Record<string, { name: string; count: number; totalTime: number; timedCount: number }> = {};

    // Helper to sanitize name
    const sanitizeName = (name: any): string => {
      if (!name || name === 'undefined' || name === 'null') {
        return '';
      }
      return String(name);
    };

    responses.forEach(r => {
      if (r.submittedBy && r.submittedBy !== 'undefined') {
        if (!byCanvasser[r.submittedBy]) {
          const name = sanitizeName(r.submittedByName) || sanitizeName(r.submittedBy) || '';
          byCanvasser[r.submittedBy] = {
            name: name || 'Anonymous',
            count: 0,
            totalTime: 0,
            timedCount: 0,
          };
        }
        byCanvasser[r.submittedBy].count++;

        // Calculate completion time if available
        if (r.startedAt && r.completedAt) {
          const start = r.startedAt?.toDate?.() || new Date();
          const end = r.completedAt?.toDate?.() || new Date();
          const minutes = (end.getTime() - start.getTime()) / 60000;
          byCanvasser[r.submittedBy].totalTime += minutes;
          byCanvasser[r.submittedBy].timedCount++;
        }
      }
    });

    return Object.entries(byCanvasser)
      .map(([userId, data]) => ({
        userId,
        userName: data.name,
        responseCount: data.count,
        avgCompletionTime: data.timedCount > 0
          ? Math.round(data.totalTime / data.timedCount)
          : 0,
      }))
      .sort((a, b) => b.responseCount - a.responseCount);
  }

  async getQuestionSummary(
    surveyId: string,
    questionId: string,
    dateRange?: DateRange
  ): Promise<QuestionAnalytics | null> {
    const survey = await surveyService.get(surveyId);
    if (!survey) return null;

    const question = survey.questions.find(q => q.id === questionId);
    if (!question) return null;

    const responses = await this.getSurveyResponses(surveyId, dateRange);
    return this.analyzeQuestion(question, responses);
  }

  async exportResponsesToCSV(
    surveyId: string,
    dateRange?: DateRange,
    includePartial: boolean = true
  ): Promise<string> {
    const survey = await surveyService.get(surveyId);
    if (!survey) return '';

    let responses = await this.getSurveyResponses(surveyId, dateRange);
    if (!includePartial) {
      responses = responses.filter(r => !r.discontinued);
    }

    if (responses.length === 0) return '';

    // Build CSV headers — resident info first so each row reads as a
    // complete record: who was surveyed, where, by whom, then all answers.
    const headers = [
      'Resident Name',
      'Phone',
      'Email',
      'Address',
      'Canvasser',
      'Date',
      'Workload',
      'Status',
      ...survey.questions.map(q => q.text),
    ];

    // Build CSV rows
    const rows = responses.map(r => {
      // Sanitize fields that may contain the literal string "undefined"
      // from older data created before the firstName/lastName bug was fixed.
      const clean = (v: any): string => {
        if (v == null) return '';
        const s = String(v);
        if (s === 'undefined' || s === 'undefined undefined') return '';
        return s;
      };

      const baseData = [
        clean(r.residentName),
        clean(r.residentPhone),
        clean(r.residentEmail),
        clean(r.addressStreet),
        clean(r.submittedByName) || clean(r.submittedBy) || 'Unknown',
        r.completedAt?.toDate?.()?.toLocaleDateString?.() || r.startedAt?.toDate?.()?.toLocaleDateString?.() || '',
        clean(r.workloadName) || clean(r.workloadId),
        r.discontinued ? 'Discontinued' : 'Completed',
      ];

      const questionData = survey.questions.map(q => {
        const raw = r.responses[q.id];
        if (raw === undefined || raw === null) return '';

        // For choice-based questions, the stored value is often the option ID
        // (a UUID). Resolve it to the human-readable option text.
        const resolveOptionId = (id: string): string => {
          if (!q.options || q.options.length === 0) return id;
          const match = q.options.find(o => o.id === id || o.value === id);
          return match ? match.text : id;
        };

        if (Array.isArray(raw)) {
          return raw.map(v => {
            const s = v == null ? '' : String(v);
            return resolveOptionId(s);
          }).filter(Boolean).join('; ');
        }

        const str = String(raw);
        if (str === 'undefined') return '';
        return resolveOptionId(str);
      });

      return [...baseData, ...questionData];
    });

    // Convert to CSV string
    const csvContent = [
      headers.map(h => `"${h.replace(/"/g, '""')}"`).join(','),
      ...rows.map(row =>
        row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      ),
    ].join('\n');

    return csvContent;
  }
}

export const surveyReportsService = new SurveyReportsService();
